/**
 * PhaseIndicator Component
 * 
 * Shows the current phase with personality icons:
 * - Phase 1: Profile Consultant (colorful logo)
 * - Phase 2: Product Expert (simple logo)
 * - Phase 3: Healthcare Manager (B&W logo) - reserved for future
 * 
 * @author Heseda AI Team
 * @version 1.0
 */

import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { calculateProfileCompletion } from '../../services/profileService';
import type { MasterProfile } from '../../types/profile';

/** Logo images for each phase personality */
const PHASE_LOGOS = {
  phase1: '/logo-512-colorful.png',    // Profile Consultant
  phase2: '/logo-1024-bw.png',          // Product Expert
  phase3: '/logo-1024_simple.png',      // Healthcare Manager (reserved)
} as const;

interface PhaseIndicatorProps {
  masterProfile: Partial<MasterProfile>;
  onDismiss?: () => void;
}

/**
 * Phase indicator component showing current assistant mode with personality logo
 * 
 * @param masterProfile - User's profile data used to calculate completion
 * @param onDismiss - Optional callback to dismiss the indicator
 */
function PhaseIndicator({ masterProfile, onDismiss }: PhaseIndicatorProps) {
  const { t } = useTranslation('profile');
  // Calculate current completion
  const completion = useMemo(() => calculateProfileCompletion(masterProfile as MasterProfile), [masterProfile]);
  
  // Determine if profile has real data (not empty/default)
  const hasRealProfile = useMemo(() => {
    const keys = Object.keys(masterProfile);
    if (keys.length === 0) return false;
    // Check if any section has actual data
    const identity = masterProfile.identity as Record<string, unknown> | undefined;
    const skin = masterProfile.skin as Record<string, unknown> | undefined;
    return !!(identity?.name || identity?.email || skin?.type || skin?.concerns);
  }, [masterProfile]);

  // The phase is determined by completion, but we need to handle the initial empty state
  // Key insight: If profile is empty/loading, we should NOT show anything until we know
  const calculatedPhase = completion >= 50 ? 'phase2' : 'phase1';
  
  // Use state to track the displayed phase - only update when we have real data
  const [displayedPhase, setDisplayedPhase] = useState<'phase1' | 'phase2' | null>(null);
  
  useEffect(() => {
    if (hasRealProfile) {
      // We have real profile data, show the correct phase
      setDisplayedPhase(calculatedPhase);
    } else if (displayedPhase === null) {
      // No real data yet and no phase set - default to phase1 for new users
      const timer = setTimeout(() => {
        if (!hasRealProfile) {
          setDisplayedPhase('phase1'); // Default to phase1 for new users
        }
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [hasRealProfile, calculatedPhase, displayedPhase]);

  // Also update if phase actually changes (user profile completion changes)
  useEffect(() => {
    if (hasRealProfile && displayedPhase !== null && displayedPhase !== calculatedPhase) {
      setDisplayedPhase(calculatedPhase);
    }
  }, [calculatedPhase, hasRealProfile, displayedPhase]);

  // Don't render until we have a phase to show
  const currentPhase = displayedPhase;
  if (!currentPhase) {
    return (
      <div className="phase-indicator phase-indicator--loading" style={{ opacity: 0 }}>
        <div className="phase-indicator__content" />
      </div>
    );
  }

  // Phase 2: hide entirely — the AI greeting + product cards already convey "Product Expert" mode.
  // This frees ~60px of vertical space and removes redundancy.
  if (currentPhase === 'phase2') return null;
  
  const isPhase1 = currentPhase === 'phase1';
  
  const phaseConfig = {
    phase1: {
      logo: PHASE_LOGOS.phase1,
      name: t('phase.profileConsultant'),
      area: t('phase.buildingProfile'),
    },
    phase2: {
      logo: PHASE_LOGOS.phase2,
      name: t('phase.productExpert'),
      area: t('phase.personalizedRecs'),
    },
  };

  const config = phaseConfig[currentPhase];

  return (
    <div className={`phase-indicator phase-indicator--${currentPhase}`} data-testid="phase-indicator">
      <div className="phase-indicator__content">
        <div className="phase-indicator__identity">
          <img 
            src={config.logo} 
            alt={config.name}
            className="phase-indicator__logo"
          />
          <div className="phase-indicator__text">
            <span className="phase-indicator__name">
              {config.name}
            </span>
            <span className="phase-indicator__area">
              {config.area}
            </span>
          </div>
        </div>
        {isPhase1 && (
          <>
            <div className="phase-indicator__progress">
              <span className="phase-indicator__progress-text">{completion}%</span>
              <div className="phase-indicator__progress-bar">
                <div 
                  className="phase-indicator__progress-fill" 
                  style={{ width: `${completion}%` }}
                ></div>
              </div>
            </div>
            {completion >= 35 && completion < 50 && (
              <span className="phase-indicator__hint">
                {t('phase.almostThere', { defaultValue: 'Almost there! Search unlocks at 50%' })}
              </span>
            )}
          </>
        )}
        {onDismiss && (
          <button
            type="button"
            className="phase-indicator__dismiss"
            onClick={onDismiss}
            aria-label="Dismiss"
          >&times;</button>
        )}
      </div>
    </div>
  );
}

export default PhaseIndicator;
